/**
 * Cart bag counters — full cart count by default; POS drawer count on shop-all.
 */
class CartCounterUpdater {
  static #CONFIG_SELECTOR = '.js-pos-cart-drawer-config';
  static #SYNC_EVENT = 'pos-cart-drawer:synced';
  static #fetchGeneration = 0;

  static isShopAllPage() {
    return document.documentElement.dataset.cartCounterMode === 'shop-all';
  }

  static #parsePosConfig() {
    const node = document.querySelector(CartCounterUpdater.#CONFIG_SELECTOR);
    if (!node?.textContent) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('POS cart drawer config parse error', error);
      return null;
    }
  }

  static #matchesFamilyProduct(families, productId) {
    const normalizedProductId = Number(productId);
    if (!Number.isFinite(normalizedProductId)) return false;

    return families.some((family) =>
      family.productIds?.some((id) => Number(id) === normalizedProductId)
    );
  }

  static getDrawerItemCount(cart, config = null) {
    const items = cart?.items;
    if (!items?.length) return 0;

    const families = (config || CartCounterUpdater.#parsePosConfig())?.families;
    if (!families?.length) return 0;

    return items.reduce((sum, item) => {
      if (!item.selling_plan_allocation) return sum;
      if (!CartCounterUpdater.#matchesFamilyProduct(families, item.product_id)) return sum;
      return sum + item.quantity;
    }, 0);
  }

  static resolveCount(cart) {
    if (!cart) return 0;
    if (!CartCounterUpdater.isShopAllPage()) return cart.item_count ?? 0;

    const drawer = window.posCartDrawerInstance;
    if (drawer && typeof drawer.getDrawerItemCount === 'function') {
      return drawer.getDrawerItemCount(cart);
    }

    return CartCounterUpdater.getDrawerItemCount(cart);
  }

  static update(cart) {
    const itemCount = CartCounterUpdater.resolveCount(cart);
    CartCounterUpdater.#updateElements(itemCount);
    CartCounterUpdater.#updateIconBubble(itemCount);
    CartCounterUpdater.#markReady();
  }

  static #markReady() {
    if (!CartCounterUpdater.isShopAllPage()) {
      document.documentElement.removeAttribute('data-cart-counter-ready');
      return;
    }

    document.documentElement.dataset.cartCounterReady = 'true';
  }

  static #updateElements(itemCount) {
    document.querySelectorAll('.cart-counter-updater').forEach((el) => {
      el.textContent = itemCount;
    });
  }

  static #updateIconBubble(itemCount) {
    const iconBubble = document.querySelector('#cart-icon-bubble');
    if (!iconBubble) return;

    const countSpan = iconBubble.querySelector('span[aria-hidden="true"]');
    if (countSpan) {
      countSpan.textContent = itemCount;
      if (itemCount === 0) iconBubble.querySelector('.cart-count-bubble')?.remove();
      return;
    }

    if (itemCount === 0) {
      iconBubble.querySelector('.cart-count-bubble')?.remove();
      return;
    }

    let bubbleDiv = iconBubble.querySelector('.cart-count-bubble');
    if (!bubbleDiv) {
      bubbleDiv = document.createElement('div');
      bubbleDiv.className = 'cart-count-bubble';
      iconBubble.appendChild(bubbleDiv);
    }

    bubbleDiv.innerHTML = `<span aria-hidden="true">${itemCount}</span><span class="visually-hidden">Cart count</span>`;
  }

  static async fetchAndUpdate() {
    const requestGeneration = ++CartCounterUpdater.#fetchGeneration;

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const cart = await response.json();
      if (requestGeneration !== CartCounterUpdater.#fetchGeneration) return;

      CartCounterUpdater.update(cart);
    } catch (error) {
      console.error('Error updating cart counter:', error);
    }
  }

  static #handlePosSync(event) {
    if (!event.detail?.cart) return;
    CartCounterUpdater.update(event.detail.cart);
  }

  static #syncFromPosDrawer() {
    const drawer = window.posCartDrawerInstance;
    if (!drawer) return false;

    drawer.refresh();
    return true;
  }

  static #initShopAll() {
    document.addEventListener(CartCounterUpdater.#SYNC_EVENT, CartCounterUpdater.#handlePosSync);

    const runSync = () => {
      if (CartCounterUpdater.#syncFromPosDrawer()) return;

      if (!document.querySelector('.js-pos-cart-drawer')) {
        CartCounterUpdater.fetchAndUpdate();
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(runSync);
    });
  }

  static init() {
    document.documentElement.removeAttribute('data-cart-counter-ready');

    if (CartCounterUpdater.isShopAllPage()) {
      CartCounterUpdater.#initShopAll();
    } else {
      CartCounterUpdater.fetchAndUpdate();
    }

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;

      if (CartCounterUpdater.isShopAllPage()) {
        CartCounterUpdater.#syncFromPosDrawer();
        return;
      }

      CartCounterUpdater.fetchAndUpdate();
    });

    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (CartCounterUpdater.isShopAllPage()) {
        CartCounterUpdater.#syncFromPosDrawer();
        return;
      }

      CartCounterUpdater.fetchAndUpdate();
    });
  }
}

window.CartCounterUpdater = CartCounterUpdater;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => CartCounterUpdater.init());
} else {
  CartCounterUpdater.init();
}
